import React from 'react';
import { ArrowRightIcon, CalculatorIcon, FileTextIcon, MessageCircleIcon, SyringeIcon } from 'lucide-react';
import { Section } from '../Section';
import { InView } from '../InView';
import { SectionHeadline } from '../SectionHeadline';
import { SnakStill } from '../../SnakStill';
import { attractChannels, convertCtas, journey } from '../../../data/deck';
import type { CtaId, StageId } from '../../../data/deck';
const CTA_ICONS: Record<CtaId, typeof FileTextIcon> = {
  evidence: FileTextIcon,
  dosing: CalculatorIcon,
  pen: SyringeIcon,
  rep: MessageCircleIcon
};
export function JourneySection() {
  return <Section id="journey" data-id="element-460" data-mp="27t09e2ow0634">
      <InView data-id="element-461" data-mp="27t8nfxwqxhfw">
        <SectionHeadline lead="From attention" follow="to action." data-id="element-462" data-mp="27w4p9xbpsq9m" />
      </InView>

      <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:gap-6" data-id="element-463" data-mp="22vqngx0pick1">
        {journey.map((stage, i) => <React.Fragment key={stage.id} data-id="element-464" data-mp="2bvn88njz6r9t" data-mpi={i}>
            <InView delay={0.12 + i * 0.12} data-id="element-465" data-mp="2bwoiipt639qh">
              <div className="flex h-[300px] items-center justify-center overflow-hidden rounded-[28px] bg-[#EFEBE2]" data-id="element-466" data-mp="2btsm55wywt45">
                <StageVisual id={stage.id} data-id="element-467" data-mp="21mbn6q331fll" />
              </div>
              <div className="mt-6 flex items-baseline gap-3" data-id="element-468" data-mp="21oo3kyt6zhwj">
                <span className="text-[10px] font-semibold tabular-nums tracking-[0.2em] text-[var(--fg)]/40" data-id="element-469" data-mp="26kmx10tdnq3t">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="text-[28px] font-extrabold uppercase leading-none tracking-[-0.01em] text-[var(--fg)]" data-id="element-470" data-mp="2fn4l7kslestg">
                  {stage.name}
                </h3>
              </div>
              <p className="mt-3 text-[14px] leading-relaxed text-[var(--fg)]" data-id="element-471" data-mp="2pbmhpptuadra">{stage.channels}</p>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-[var(--muted)]" data-id="element-472" data-mp="2ubse1jb20ofp">{stage.goal}</p>
            </InView>
            {i < journey.length - 1 && <span aria-hidden="true" className="hidden h-[300px] items-center text-[var(--fg)]/35 lg:flex" data-id="element-473" data-mp="2z893qhxwjauw">
                <ArrowRightIcon className="h-5 w-5" data-id="element-474" data-mp="1476y0jdwhpdv" />
              </span>}
          </React.Fragment>)}
      </div>

      <InView delay={0.4} data-id="element-475" data-mp="z8vqifb2vqyt">
        <p className="mt-14 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--fg)]/10 pt-8 text-[24px] font-extrabold leading-[1.15] tracking-[-0.03em] text-[var(--fg)] sm:text-[30px]" data-id="element-476" data-mp="z9nmvsci1sq5">
          <span data-id="element-477" data-mp="187hfas758281">Start with the patient.</span>
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[var(--brand)]" data-id="element-478" data-mp="1mxexmrie5xpb" />
          <span data-id="element-479" data-mp="1hykf7qoc6b5q">Simplify the decision.</span>
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[var(--brand)]" data-id="element-480" data-mp="1hxsigcj067dv" />
          <span data-id="element-481" data-mp="1vxbql5btdiq6">Drive meaningful actions.</span>
        </p>
      </InView>
    </Section>;
}
function StageVisual({
  id
}: {
  id: StageId;
}) {
  if (id === 'attract') {
    return <div className="flex flex-col items-center gap-3" data-id="element-482" data-mp="1jsnlfz0l7xh">
        <SnakStill screen={0} alt="Teaser hook: two patients — same approach?" className="h-[200px] w-[104px]" data-id="element-483" data-mp="1met7a2chdd2" />
        <div className="flex max-w-[240px] flex-wrap justify-center gap-1.5" data-id="element-484" data-mp="3tqhdkw7l0j9g">
          {attractChannels.map(channel => <span key={channel} className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-[var(--fg)] ring-1 ring-[var(--fg)]/10" data-id="element-485" data-mp="3nmb1d8uf9ghc">
              {channel}
            </span>)}
        </div>
      </div>;
  }
  if (id === 'convince') {
    return <div className="flex items-end gap-3" data-id="element-486" data-mp="2zv5nv0byf0a7">
        <SnakStill screen={2} alt="Mini-simulator: change AMH and weight" className="h-[190px] w-[98px] -rotate-3" data-id="element-487" data-mp="2zyjrsd09nhjr" />
        <SnakStill screen={3} alt="REKOVELLE reveal: an individualized starting dose" className="h-[236px] w-[122px]" data-id="element-488" data-mp="2qvioek2sc4js" />
        <SnakStill screen={4} alt="Pen screen: the dose set on the REKOVELLE pen" className="h-[190px] w-[98px] rotate-3" data-id="element-489" data-mp="2vu3pelpv2s9a" />
      </div>;
  }
  return <div className="grid w-[270px] grid-cols-2 gap-2 rounded-[18px] bg-[var(--brand)] p-3" data-id="element-490" data-mp="p22sp8rubzhy">
      {convertCtas.map(cta => {
      const Icon = CTA_ICONS[cta.id];
      return <span key={cta.id} className="flex items-center gap-2 rounded-[10px] border border-white/60 px-2.5 py-2.5 text-[11.5px] font-semibold text-white" data-id="element-491" data-mp="2q5ltqaw79xs4">
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-492" data-mp="2g6mjwozp7w3j" />
            {cta.short}
          </span>;
    })}
    </div>;
}